// HomeProofStrip — single consolidated proof + supply line.
//
// Replaces (HomeTrustBar + SignalSummaryLine + HomeSupplyLine + status pill)
// above the fold. One row, three slots:
//   1. 30d hit rate (only when resolved >= MIN, otherwise "Building ledger")
//   2. Live edges available right now
//   3. Last scan freshness
//
// Strict honesty: never invent numbers, never fabricate hit rate from <30
// resolved calls. Three slots fail independently. Polymarket-only.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, ShieldCheck, Radio } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const MIN_RESOLVED = 30;

interface Stats {
  hitRate: number | null;
  resolvedCount: number;
  liveEdges: number | null;
  lastScanIso: string | null;
}

function fmtPct(v: number | null): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${Math.round(v * 100)}%`;
}

function timeAgo(iso: string | null): string {
  if (!iso) return "—";
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const m = Math.round(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  return `${h}h ago`;
}

export default function HomeProofStrip() {
  const [stats, setStats] = useState<Stats | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [scoreboardRes, supplyRes] = await Promise.all([
          supabase.rpc("get_engine_scoreboard_split"),
          supabase
            .from("market_feed")
            .select("scanned_at, is_actionable")
            .eq("platform", "polymarket")
            .eq("platform_visibility", "visible")
            .order("scanned_at", { ascending: false })
            .limit(300),
        ]);
        if (cancelled) return;

        const sbRows = (scoreboardRes.data ?? []) as Array<{
          window_label: "7d" | "30d";
          resolved: number;
          hit_rate: number | null;
        }>;
        const w30 = sbRows.find((r) => r.window_label === "30d");

        const supplyRows = (supplyRes.data ?? []) as Array<{
          scanned_at: string;
          is_actionable: boolean;
        }>;
        const liveEdges = supplyRows.filter((r) => r.is_actionable).length;
        const lastScan = supplyRows[0]?.scanned_at ?? null;

        setStats({
          hitRate: w30?.resolved && w30.resolved >= MIN_RESOLVED ? w30.hit_rate : null,
          resolvedCount: w30?.resolved ?? 0,
          liveEdges,
          lastScanIso: lastScan,
        });
      } catch {
        if (!cancelled) setStats({ hitRate: null, resolvedCount: 0, liveEdges: null, lastScanIso: null });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <Link
      to="/track-record"
      className="block rounded-xl border border-border/60 bg-card/60 hover:border-border transition-colors mb-4"
      aria-label="View public track record"
    >
      <div className="grid grid-cols-3 divide-x divide-border/40">
        <Slot
          icon={<ShieldCheck className="w-3.5 h-3.5 text-primary" />}
          label="30d hit rate"
          value={
            stats?.hitRate != null
              ? fmtPct(stats.hitRate)
              : stats == null
                ? "—"
                : "Building"
          }
          sub={
            stats?.hitRate != null
              ? `${stats.resolvedCount} resolved`
              : stats == null
                ? "Loading"
                : `${stats.resolvedCount}/${MIN_RESOLVED} sample`
          }
        />
        <Slot
          icon={<Activity className="w-3.5 h-3.5 text-primary" />}
          label="Live edges"
          value={stats?.liveEdges != null ? String(stats.liveEdges) : "—"}
          sub="Actionable now"
          accent
        />
        <Slot
          icon={
            <span className="relative inline-flex">
              <span className="absolute inline-flex h-1.5 w-1.5 rounded-full bg-primary opacity-60 animate-ping" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
            </span>
          }
          label="Last scan"
          value={stats?.lastScanIso ? timeAgo(stats.lastScanIso) : "—"}
          sub="5min cycle"
        />
      </div>
    </Link>
  );
}

function Slot({
  icon,
  label,
  value,
  sub,
  accent = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
  accent?: boolean;
}) {
  return (
    <div className="px-3 py-2.5">
      <div className="flex items-center gap-1.5 mb-1">
        {icon}
        <span className="text-[9.5px] font-body uppercase tracking-widest text-muted-foreground">
          {label}
        </span>
      </div>
      <div
        className={`text-base font-display font-bold leading-none tabular-nums ${
          accent ? "text-primary" : "text-foreground"
        }`}
      >
        {value}
      </div>
      <div className="text-[10px] font-body text-muted-foreground mt-1 leading-snug">
        {sub}
      </div>
    </div>
  );
}
